const swea = require('swea');
const InstructionServiceActions = swea.react.actions.InstructionServiceActions;
const PrimitiveModel = swea.scene.models.PrimitiveModel;
const PickPlane = swea.scene.primitives.PickPlane;
const ViewerManager = swea.editor.ViewerManager;
const ModelManager = swea.editor.ModelManager;

/**
 * Interaction for creating section volumes.
 * Based on solid area interaction but always extrudable and uses section-specific color.
 *
 * @class
 * @extends { TextAreaInteraction }
 */
class CustomSectionVolumeInteraction extends swea.editor.interactions.TextAreaInteraction {
  /**
   * The constructor.
   * @param { object } options An option object, see {@link TextAreaInteraction} for more info.
   * @param { number[] } options.baseBoundsMin Bounding box min [x,y,z] of the base model (world space).
   * @param { number[] } options.baseBoundsMax Bounding box max [x,y,z] of the base model (world space).
   */
  constructor(options) {
    super(options);
    this._disabledInteractionClasses = [];
    this._floorPlaneModel = undefined;
  }

  /**
   * Initializes the interaction.
   * @param element the element that the interaction listens to.
   * @param viewerId the viewer id of the viewer the interaction operates in.
   */
  initialize(element, viewerId) {
    super.initialize(element, viewerId);

    // When user closes the tool
    this.addTrigger(new swea.editor.interactions.EventHandler(
      [swea.editor.interactions.eventCodes.ESC],
      this,
      () => {
        this.terminate();
      }
    ));

    // When user wants to remove last point
    this.addTrigger(new swea.editor.interactions.EventHandler(
      [swea.editor.interactions.eventCodes.BACK_SPACE],
      this,
      () => {
        this.removeLastPoint();
      }
    ));
  }

  /**
   * When the interaction is initialized, create an invisible floor plane at the model's
   * bounding box minZ and show instruction.
   * @param element { DOMElement } The element that the interaction works on
   * @param viewerId { number } The viewerId
   * @override
   */
  whenInitialized(element, viewerId) {
    this._disabledInteractionClasses = swea.editor.InteractionManager.instance().disableContinuousInteractions();
    InstructionServiceActions.showInstruction('instructions.create_section_volume');

    // Create an invisible floor plane at minZ so clicks outside the model still produce a hit
    const bMin = this._options.baseBoundsMin;
    const bMax = this._options.baseBoundsMax;
    if (bMin && bMax) {
      const extentX = bMax[0] - bMin[0];
      const extentY = bMax[1] - bMin[1];
      const planeSize = Math.max(extentX, extentY) * 2.0;
      const centerX = (bMin[0] + bMax[0]) / 2.0;
      const centerY = (bMin[1] + bMax[1]) / 2.0;

      this._floorPlaneModel = new PrimitiveModel({
        nodeOptions: {
          pickable: true,
          initialPosition: [centerX, centerY, bMin[2]]
        }
      });
      this._floorPlaneModel.addPrimitive(new PickPlane({
        width: planeSize,
        nodeOptions: {
          rotationNormal: [0, 0, 1]
        }
      }));
      ViewerManager.instance().addModelToViewer(this._floorPlaneModel.getId(), viewerId);
    }
  }

  /**
   * Terminates the interaction and removes the invisible floor plane.
   * @override
   */
  terminate() {
    // Remove the floor plane if it was created
    if (this._floorPlaneModel) {
      const viewerId = this.getViewerId();
      if (viewerId !== undefined) {
        ViewerManager.instance().removeModelFromViewer(this._floorPlaneModel.getId(), viewerId);
      }
      ModelManager.instance().removeModel(this._floorPlaneModel.getId());
      this._floorPlaneModel = undefined;
    }

    super.terminate();
    swea.editor.InteractionManager.instance().enableContinuousInteractions(this._disabledInteractionClasses);
    InstructionServiceActions.showInstruction('instructions.corner_interaction');
  }
}
export default CustomSectionVolumeInteraction;
